// CLOSED-LOOPS: the Inbox's commitments — the ones asked of the caller and the ones they asked
// (contracts/api/commitments.ts). Keys sit under `["company", co]`, so a change ping refreshes
// them; a move invalidates both sides.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";

export type Commitment = OperationOutput<"commitments.get">;
export type CommitmentSide = "of_me" | "by_me";
export type CommitmentActionBody = OperationInput<"commitments.act">["body"];

export const commitmentKeys = {
  all: (co: string) => ["company", co, "commitments"] as const,
  list: (co: string, side: CommitmentSide) => ["company", co, "commitments", side] as const,
};

export const commitmentsQuery = (api: Api, co: string, side: CommitmentSide) =>
  queryOptions({
    queryKey: commitmentKeys.list(co, side),
    queryFn: () => api.client.commitments.list({ params: { co }, query: { side, open: "true" } }),
    staleTime: 30_000,
  });

/** One move on a commitment; both lists refresh after it. */
export function useCommitmentAct(api: Api, co: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CommitmentActionBody }) =>
      api.client.commitments.act({ params: { co, id }, body }),
    onSettled: () => qc.invalidateQueries({ queryKey: commitmentKeys.all(co) }),
  });
}
